// Copyright (c) Docugami, Inc. All rights reserved.

import { addBindingThunk } from "components/reports/reportsActions";
import { ChunkResizeEvent } from "messaging/messages/ChunkResizeEvent";
import { MessageId } from "messaging/MessageTypes";
import { ReportBinding } from "networking/reportsApi";
import { useMessageHandler } from "./MessageHandler";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectEntries, selectReportDocumentId, selectReportEntryBindingsForCurrentDocument, selectReportId } from "store/reports/selectors";
import { selectAllBindings } from "store/reports/reportsSlice";

/**
 * Hook for handling the special case where a resize event actually results in a newly created chunk.
 * Any report entry in the given report bound to the original chunk will be updated and bound to the
 * newly created chunk instead.
 */
export function useReportResizeHandler() {
    const entries = useAppSelector(selectEntries);
    const documentBindings = useAppSelector(selectReportEntryBindingsForCurrentDocument);
    const reportId = useAppSelector(selectReportId);
    const folderId = useAppSelector(selectReportDocumentId);

    useMessageHandler(MessageId.ChunkResizeEvent, onChunkResized);
    const dispatch = useAppDispatch();

    async function onChunkResized(event: ChunkResizeEvent) {
        if (!documentBindings || !entries || folderId !== event.original.folderId) {
            return;
        }

        let targetBinding: ReportBinding | undefined;
        for (const entryBindings of selectAllBindings(documentBindings)) {
            targetBinding = entryBindings.bindings.find(x => x.spanId === event.original.spanId);
            if (!!targetBinding) {
                break;
            }
        }

        if (!!targetBinding) {
            if (event.original.spanId !== event.result.spanId) {
                // Chunk instance for this binding has changed, so replace it
                const entry = entries?.entities[targetBinding.entryId];
                if (!entry) {
                    return;
                }

                dispatch(addBindingThunk(reportId, {
                    folderId: folderId,
                    entryId: targetBinding.entryId,
                    instanceId: event.result.spanId!,
                    prevInstanceId: targetBinding.spanId!
                }));
            }
        }
    }
}
